import ShowErrorsFormItem from '@aims/shared/components/ShowErrorsFormItem';
import { HolisticQuarterlyReportItems } from '@aims/shared/reports';
import {
  ReportApprovalStatuses,
  ReportTypes,
} from '@aims/shared/reports/constants';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  Button,
  Form,
  Input,
  Skeleton,
  Space,
  Typography,
  notification,
} from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useCallback, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CSPage from '../../../components/CSPage';
import CSPageHeader from '../../../components/CSPageHeader';
import settings from '../../../settings';
import useProfile from '../../../shared/use-profile';
import useHolisticProjectForCoord from '../use-holistic-project-for-coord';
import DeleteReportButton from './DeleteReportButton';
import {
  createHolisticQuarterlyReportMutation,
  submitHolisticQuarterlyReportMutation,
  unsubmitHolisticQuarterlyReportMutation,
  updateHolisticQuarterlyReportMutation,
} from './constants';
import useReport from './use-report';

const { Title, Text } = Typography;

const queryId = 'editHolisticQuarterlyReportPage';

function EditHolisticQuarterlyReportPage() {
  const { projectId, reportId } = useParams();
  const profile = useProfile();

  const { loading: loading1, data: holisticProject } =
    useHolisticProjectForCoord(projectId, queryId);

  const {
    report,
    loading: loading2,
    refetch,
  } = useReport(reportId, queryId, false);

  const [form] = Form.useForm();
  const finishSubForm = useRef();
  const shouldSubmitReport = useRef(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [updateReport] = useMutation(updateHolisticQuarterlyReportMutation);
  const [createReport] = useMutation(createHolisticQuarterlyReportMutation);
  const [submitReport] = useMutation(submitHolisticQuarterlyReportMutation);
  const [unsubmitReport] = useMutation(unsubmitHolisticQuarterlyReportMutation);
  const history = useHistory();

  const onSubmit = useCallback(
    async (__values) => {
      let msg;
      if (shouldSubmitReport.current === 'submit') {
        msg = 'Submitting Report ...';
      } else if (shouldSubmitReport.current === 'resubmit') {
        msg = 'Re-submitting Proposal ...';
      } else if (shouldSubmitReport.current === 'unsubmit') {
        msg = 'Unsubmitting Report ...';
      } else {
        msg = 'Saving Draft ...';
      }
      setSaveLoading(msg);
      const { notes, ..._values } = __values;
      try {
        const reportDetails = finishSubForm.current(_values);
        if (
          report &&
          ['unsubmit', 'resubmit'].includes(shouldSubmitReport.current)
        ) {
          await unsubmitReport({
            variables: {
              holisticProjectId: projectId,
              reportId,
            },
          });
          notification.success({
            message: 'Success',
            description: 'Report unsubmitted.',
          });
        }
        if (report) {
          await updateReport({
            variables: {
              holisticProjectId: projectId,
              report: {
                _id: reportId,
                reportType: ReportTypes.HOLISTIC_PROGRESS.key,
                notes,
                contactId: profile.contactId,
                ...reportDetails,
              },
            },
          });
        } else {
          await createReport({
            variables: {
              holisticProjectId: projectId,
              report: {
                _id: reportId,
                contactId: profile.contactId,
                reportType: ReportTypes.HOLISTIC_PROGRESS.key,
                ...reportDetails,
              },
            },
          });
        }
        if (['submit', 'resubmit'].includes(shouldSubmitReport.current)) {
          await submitReport({
            variables: {
              holisticProjectId: projectId,
              reportId,
            },
          });
          notification.success({
            message: 'Success',
            description: 'Report submitted.',
          });
          history.push(
            `/holistic-projects/${projectId}/reports/view/${reportId}`,
          );
        } else {
          notification.success({
            message: 'Success',
            description: 'Report saved successfully',
          });
          refetch();
        }
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Error',
          description: 'There was an error saving your report.',
        });
      }
      setSaveLoading(false);
    },
    [
      reportId,
      createReport,
      history,
      profile,
      report,
      submitReport,
      unsubmitReport,
      updateReport,
      projectId,
      refetch,
    ],
  );
  const title = 'Holistic Project Quarterly Report';
  const loading = loading1 || loading2 || saveLoading;
  const showDelete = report && !report.submittedAt;

  return (
    <CSPage title="AIMS.org | Edit Quarterly Holistic Project Report">
      <CSPageHeader
        titleComponent={null}
        title={title}
        parents={[
          {
            label: 'My Projects',
            link: `/holistic-projects`,
          },
          {
            label: holisticProject?.projectName,
            link: `/holistic-projects/${projectId}/reports`,
          },
        ]}
      />
      <Form
        layout="vertical"
        onFinish={onSubmit}
        id="EditReportForm"
        form={form}
        style={{ maxWidth: 800, marginRight: 'auto', marginLeft: 'auto' }}
      >
        {(loading1 || loading2) && (
          <>
            <Title level={3} style={{ textAlign: 'center' }}>
              {title}
            </Title>
            <Skeleton active title paragraph />
            <Form.Item>
              <Input disabled />
            </Form.Item>
            <Skeleton active title />
            <Form.Item>
              <Input disabled />
            </Form.Item>
            <Skeleton active title />
            <Form.Item>
              <Input disabled />
            </Form.Item>
            <Skeleton active title paragraph />
            <Form.Item>
              <Input disabled />
            </Form.Item>
            <Skeleton active title paragraph />
          </>
        )}
        {!loading1 && !loading2 && holisticProject && (
          <>
            <HolisticQuarterlyReportItems
              _id={reportId}
              profile={profile}
              report={report}
              loading={loading}
              form={form}
              finishSubForm={finishSubForm}
              colors={settings.colors}
              holisticProject={holisticProject}
              showTitle
              fieldsToSet={{ holisticProjectId: projectId }}
            />
          </>
        )}
        <ShowErrorsFormItem />
        <div style={{ height: 16 }} />
        <Form.Item>
          <Space
            style={{
              width: '100%',
              justifyContent: 'center',
            }}
          >
            {(!report || (report && !report.submittedAt)) && (
              <Button
                key="draft"
                type="dashed"
                loading={loading}
                htmlType="button"
                onClick={() => {
                  shouldSubmitReport.current = undefined;
                  form.submit();
                }}
              >
                Save Draft
              </Button>
            )}
            {(!report || (report && !report.submittedAt)) && (
              <Button
                key="submit"
                type="primary"
                loading={loading}
                htmlType="button"
                onClick={() => {
                  shouldSubmitReport.current = 'submit';
                  form.submit();
                }}
              >
                Submit Report
              </Button>
            )}
            {report?.submittedAt &&
              (!report.approval ||
                (report.approval && report.approval.status === 'PENDING')) && (
                <Button
                  key="unsubmit"
                  type="dashed"
                  loading={loading}
                  htmlType="button"
                  onClick={() => {
                    shouldSubmitReport.current = 'unsubmit';
                    form.submit();
                  }}
                >
                  Unsubmit Report
                </Button>
              )}
            {report?.status === ReportApprovalStatuses.HOLD.key && (
              <Button
                key="resubmit"
                type="primary"
                loading={loading}
                htmlType="button"
                onClick={() => {
                  shouldSubmitReport.current = 'resubmit';
                  form.submit();
                }}
              >
                Save Changes & Re-Submit Report
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
      {showDelete && (
        <DeleteReportButton report={report} holisticProjectId={projectId} />
      )}
      <Modal open={saveLoading} footer={null} closable={false} width={300}>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <LoadingOutlined style={{ fontSize: 32, marginBottom: 16 }} />
          <Title level={4}>{saveLoading}</Title>
        </div>
      </Modal>
    </CSPage>
  );
}

export default EditHolisticQuarterlyReportPage;
